import { matchPath, parsePath } from "react-router";

import { queryValue } from "../../app/navigation.js";
import { withReturnUrl } from "../../app/return-after-register.js";
import { accountUrls } from "../account/urls.js";

/**
 * 契約の中の利用者の URL（画面と API）。
 *
 * <b>画面の URL は今のアプリと同じ形</b>（Program.cs の <c>TenantUser</c> ルート。
 * 招待メールに載っているため変えられない。<c>Core/Services/InvitationLink.cs</c>）。
 * 対象の利用者（メールアドレス）は、画面ではパスの最後、API ではクエリの <c>id</c> で渡す。
 */

const page = (tenantId: number, action: string): string =>
    `/tenant/Edit/${String(tenantId)}/TenantUser/${action}`;

const api = (tenantId: number, path = ""): string => `/api/tenants/${String(tenantId)}/users${path}`;

const withId = (path: string, email: string): string => `${path}?id=${encodeURIComponent(email)}`;

/** 一覧の続きの読み込み。<b>絞り込みの条件は引き継ぐ</b>（外れると探し直しになる）。 */
const more = (tenantId: number, keyword: string | null, status: number | null, take: number): string => {
    const query = new URLSearchParams();
    if (keyword !== null && keyword !== "") {
        query.set("Keyword", keyword);
    }
    if (status !== null) {
        query.set("Status", String(status));
    }
    query.set("Take", String(take));
    return `${page(tenantId, "Index")}?${query.toString()}#more`;
};

/** 招待の承諾の画面の形（routes.tsx の <c>Register</c> と同じ。大文字小文字は問わない）。 */
const registerPattern = "/tenant/Edit/:tenantId/TenantUser/Register";

/**
 * 戻り先が招待の承諾の画面（招待メールのリンク）なら、その合言葉。ほかの画面・合言葉が無ければ undefined。
 *
 * <b>契約が数でなければ招待の形と見ない</b>（TenantScoped と同じ）。<b>合言葉の値は確かめない</b>——
 * 招待と合うかはサーバ（RegistrationFlow）が確かめる。戻り先はこの製品の中のパスに限ったもの（localReturnUrl）を渡す。
 */
export const invitationPassphrase = (returnUrl: string): string | undefined => {
    const { pathname = "", search = "" } = parsePath(returnUrl);
    const tenantId = matchPath(registerPattern, pathname)?.params.tenantId;
    if (tenantId === undefined || !/^\d+$/u.test(tenantId)) {
        return undefined;
    }
    const passphrase = queryValue(new URLSearchParams(search), "passphrase");
    return passphrase === undefined || passphrase === "" ? undefined : passphrase;
};

/**
 * アカウント登録の画面の URL。戻り先はそのまま付ける。<b>戻り先が招待の承諾の画面なら、その合言葉も
 * 登録画面の ?passphrase= に付ける</b>——招待と合えば確認メールなしで承諾画面へ進む（判定はサーバの RegistrationFlow）。
 * ログイン画面の「アカウント登録」と、未ログインで招待のリンクを開いたときの行き先（register-page.tsx）で使う。
 */
export const accountRegisterUrl = (returnUrl: string | undefined): string => {
    const passphrase = returnUrl === undefined ? undefined : invitationPassphrase(returnUrl);
    return passphrase === undefined || returnUrl === undefined
        ? withReturnUrl(accountUrls.register, returnUrl)
        : `${accountUrls.register}?${new URLSearchParams({ passphrase, returnUrl }).toString()}`;
};

export const tenantUserUrls = {
    index: (tenantId: number): string => page(tenantId, "Index"),
    more,
    edit: (tenantId: number, email: string): string => page(tenantId, `Edit/${encodeURIComponent(email)}`),
    delete: (tenantId: number, email: string): string => page(tenantId, `Delete/${encodeURIComponent(email)}`),
    contractor: (tenantId: number): string => page(tenantId, "Contractor"),
    contractorConfirm: (tenantId: number, email: string): string =>
        page(tenantId, `ContractorConfirm/${encodeURIComponent(email)}`),
    /** 契約一覧（React の画面）。 */
    tenants: "/Tenant",
    /** 契約情報（引き継ぎの画面から戻る先）。 */
    tenantDetail: (tenantId: number): string => `/Tenant/Detail/${String(tenantId)}`,

    api: {
        /** 一覧。<b>画面の URL のクエリをそのまま渡す</b>（条件の読み方はサーバが決める）。 */
        list: (tenantId: number, search: string): string => `${api(tenantId)}${search}`,
        invite: (tenantId: number): string => api(tenantId, "/invite"),
        edit: (tenantId: number, email: string): string => withId(api(tenantId, "/edit"), email),
        delete: (tenantId: number, email: string): string => withId(api(tenantId, "/delete"), email),
        contractor: (tenantId: number): string => api(tenantId, "/contractor"),
        contractorConfirm: (tenantId: number, email: string): string =>
            withId(api(tenantId, "/contractor/confirm"), email),
        register: (tenantId: number, passphrase?: string): string =>
            passphrase === undefined
                ? api(tenantId, "/register")
                : `${api(tenantId, "/register")}?passphrase=${encodeURIComponent(passphrase)}`,
    },
} as const;
